// Service icons mapping
import { Home, Car, Sun, DoorOpen, Trees, Fence, Flame, Droplets, LayoutGrid, Layers } from "lucide-react";

export const SERVICE_ICONS = {
  siding: Home,
  driveway: Car,
  windows: LayoutGrid,
  gutters: Droplets,
  entries: DoorOpen,
  sidewalk: LayoutGrid,
  patio: Trees,
  deck: Layers,
  fence: Fence,
  roof: Sun,
  chimney: Flame,
};

// Service order for display (1-11 as specified)
export const SERVICE_ORDER = [
  'siding',      // 1
  'driveway',    // 2
  'windows',     // 3
  'gutters',     // 4
  'entries',     // 5
  'sidewalk',    // 6
  'patio',       // 7
  'deck',        // 8
  'fence',       // 9
  'roof',        // 10
  'chimney',     // 11
];

export const CALLOUT_POSITIONS = {
  siding: { top: '30%', left: '12%' },
  driveway: { bottom: '10%', left: '30%' },
  windows: { top: '40%', right: '10%' },
  gutters: { top: '15%', left: '25%' },
  entries: { top: '55%', left: '45%' },
  sidewalk: { bottom: '5%', left: '50%' },
  patio: { bottom: '25%', right: '12%' },
  deck: { bottom: '30%', right: '25%' },
  fence: { top: '65%', left: '5%' },
  roof: { top: '5%', left: '35%' },
  chimney: { top: '12%', right: '30%' },
};

export const TIME_SLOTS = [
  { id: "8A-12N", label: "Morning (8AM - 12PM)" },
  { id: "12N-4P", label: "Afternoon (12PM - 4PM)" },
];

// Window pricing tiers - updated
export const WINDOW_TIERS = [
  { min: 1, max: 15, price: 12, label: "1-15 windows @ $12/each" },
  { min: 16, max: 25, price: 10, label: "16-25 windows @ $10/each" },
  { min: 26, max: 100, price: 8, label: "26+ windows @ $8/each" },
];

// Get window price per unit based on count - updated
export const getWindowPricePerUnit = (count) => {
  if (count <= 15) return 15;
  if (count <= 25) return 12;
  return 10;
};

// Patio size options - updated prices
export const PATIO_SIZES = [
  { value: "10x10", label: "10' x 10' (100 sq ft)", price: 50 },
  { value: "10x20", label: "10' x 20' (200 sq ft)", price: 75 },
  { value: "20x20", label: "20' x 20' (400 sq ft)", price: 100 },
  { value: "20x30", label: "20' x 30' (600 sq ft)", price: 150 },
  { value: "larger", label: "Larger (Contact for quote)", price: 0 },
];

// Deck size options
export const DECK_SIZES = [
  { value: "10x10", label: "10' x 10' (100 sq ft)", price: 50 },
  { value: "10x20", label: "10' x 20' (200 sq ft)", price: 100 },
  { value: "20x20", label: "20' x 20' (400 sq ft)", price: 150 },
  { value: "20x30", label: "20' x 30' (600 sq ft)", price: 200 },
  { value: "larger", label: "Larger (Contact for quote)", price: 0 },
];

// Fence linear feet options - updated prices
export const FENCE_TIERS = [
  { min: 0, max: 100, price: 75, label: "100 LF or less" },
  { min: 101, max: 200, price: 125, label: "101-200 LF" },
  { min: 201, max: 300, price: 175, label: "201-300 LF" },
  { min: 301, max: 400, price: 200, label: "301-400 LF" },
  { min: 401, max: 9999, price: 325, label: "Over 400 LF" },
];

// Walkway/Entries linear feet tiers - NEW
export const WALKWAY_TIERS = [
  { min: 0, max: 50, price: 75, label: "50' or less" },
  { min: 51, max: 100, price: 100, label: "51'-100'" },
  { min: 101, max: 150, price: 125, label: "101'-150'" },
  { min: 151, max: 200, price: 150, label: "151'-200'" },
  { min: 201, max: 9999, price: 175, label: "Over 200'" },
];

// Get walkway price based on linear feet
export const getWalkwayPrice = (linearFeet) => {
  const tier = WALKWAY_TIERS.find(t => linearFeet >= t.min && linearFeet <= t.max);
  return tier?.price || 175;
};

// Chimney condition options - updated prices
export const CHIMNEY_CONDITIONS = [
  { value: "small_moderate", label: "Small & Moderate Stains", price: 45 },
  { value: "small_dirty", label: "Small & Dirty", price: 75 },
  { value: "large_moderate", label: "Large & Moderate Stains", price: 100 },
  { value: "large_dirty", label: "Large & Dirty", price: 150 },
  { value: "other", label: "Other (Contact for quote)", price: 0 },
];

// Siding types with scaling upcharges based on house size
export const SIDING_TYPES = [
  { value: "standard", label: "Standard", sublabel: "Vinyl/Aluminum" },
  { value: "stucco", label: "Stucco" },
  { value: "wood", label: "Brick/Wood" },
];

// Get siding upcharge based on type and square footage
export const getSidingUpcharge = (sidingType, sqft) => {
  if (sidingType === 'standard') return 0;
  
  // Stucco upcharges by house size
  const stuccoUpcharges = {
    2000: 50,
    3000: 75,
    4000: 100,
    5000: 125,
    5001: 150
  };
  
  // Wood upcharges by house size
  const woodUpcharges = {
    2000: 75,
    3000: 100,
    4000: 150,
    5000: 200,
    5001: 250
  };
  
  const upcharges = sidingType === 'stucco' ? stuccoUpcharges : woodUpcharges;
  
  if (sqft <= 2000) return upcharges[2000];
  if (sqft <= 3000) return upcharges[3000];
  if (sqft <= 4000) return upcharges[4000];
  if (sqft <= 5000) return upcharges[5000];
  return upcharges[5001];
};

// Get siding upcharge label based on type and sqft
export const getSidingUpchargeLabel = (sidingType, sqft) => {
  if (sidingType === 'standard') return '';
  const upcharge = getSidingUpcharge(sidingType, sqft);
  return `+$${upcharge} ${sidingType} upcharge`;
};

export const LOGO_URL = "/assets/logo_instaquote.png";

// Service Area ZIP Codes
// DEFINITIVE ZIP CODE LIST (April 2026) - Must match backend server.py
export const SERVICE_AREA_ZIPS = new Set([
  // DuPage County & Surrounding Areas
  "60101", "60104", "60119", "60124", "60126", "60130", "60134", "60137",
  "60139", "60144", "60147", "60148", "60151", "60153", "60154", "60155",
  "60162", "60163", "60174", "60175", "60181", "60183", "60185", "60187",
  "60188", "60189", "60190", "60191",
  // Oak Park Area
  "60301", "60302", "60303", "60304",
  // Cook County South
  "60402", "60455", "60457", "60458", "60459", "60499", "60501",
  // Bolingbrook Area
  "60440", "60490",
  // Aurora & Kane County (includes 60505, 60506)
  "60502", "60503", "60504", "60505", "60506", "60510", "60511", "60512", "60514", "60515",
  "60516", "60517", "60521", "60523", "60526", "60532", "60538", "60540",
  "60542", "60543", "60545", "60554", "60555", "60558", "60559", "60560",
  "60563", "60564", "60565",
  // Chicago (Downtown/South)
  "60605", "60606", "60629", "60632", "60638", "60804"
]);

export const isInServiceArea = (zipCode) => {
  return SERVICE_AREA_ZIPS.has(zipCode);
};

// Format phone number with parentheses around area code
export const formatPhoneNumber = (phone) => {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits[0] === '1') {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return phone;
};
